import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { useToast } from '@/contexts/admin/ToastContext';
import { extractErrorMessage } from '@/utils/common/errorHandling';
import {
  usePutCreditCardMutation,
  usePutBankAccountMutation,
  useDeleteCreditCardMutation,
  useDeleteBankAccountMutation,
  useGetPaymentMethodsQuery,
} from '@/store/api';
import { PaymentMethodCard } from '@/components/residents/redesign/payments/PaymentMethodCard';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import type { PaymentMethod } from '@/types/residents/payment';

const CreditCardIcon = RESIDENTS_SVG_COMPONENTS.CREDIT_CARD;

interface ManageWalletContentProps {
  paymentMethods: PaymentMethod[];
  onMethodsUpdated: () => void;
}

/**
 * Manage Wallet Content - Displays and manages saved payment methods
 * Uses the unified redesign PaymentMethodCard with interactive mode.
 */
export const ManageWalletContent: React.FC<ManageWalletContentProps> = ({
  paymentMethods,
  onMethodsUpdated,
}) => {
  const { colors } = useThemeColors();
  const styles = useManageWalletStyles();
  const { showSuccess, showError } = useToast();
  const { refetch } = useGetPaymentMethodsQuery();

  const [setCardDefault, { isLoading: settingCardDefault }] = usePutCreditCardMutation();
  const [setBankDefault, { isLoading: settingBankDefault }] = usePutBankAccountMutation();
  const [deleteCard, { isLoading: deletingCard }] = useDeleteCreditCardMutation();
  const [deleteBank, { isLoading: deletingBank }] = useDeleteBankAccountMutation();

  const [methodToDelete, setMethodToDelete] = useState<PaymentMethod | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isLoading = settingCardDefault || settingBankDefault || deletingCard || deletingBank;

  const handleSetDefault = useCallback(async (method: PaymentMethod) => {
    try {
      const sourceId = method.sourceId || method.id;
      if (method.type === 'card') {
        await setCardDefault({ sourceId, creditCard: { isDefault: true } }).unwrap();
      } else {
        await setBankDefault({ sourceId, bankAccount: { isDefault: true } }).unwrap();
      }
      showSuccess('Default payment method updated');
      refetch();
    } catch (error) {
      showError(extractErrorMessage(error, 'Failed to update default payment method'));
    }
  }, [setCardDefault, setBankDefault, showSuccess, showError, refetch]);

  const handleDeletePress = useCallback((method: PaymentMethod) => {
    setMethodToDelete(method);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!methodToDelete) return;
    try {
      const sourceId = methodToDelete.sourceId || methodToDelete.id;
      if (methodToDelete.type === 'card') {
        await deleteCard({ sourceId }).unwrap();
      } else {
        await deleteBank({ sourceId }).unwrap();
      }
      showSuccess('Payment method removed');
      setShowDeleteConfirm(false);
      setMethodToDelete(null);
      refetch();
      onMethodsUpdated();
    } catch (error) {
      showError(extractErrorMessage(error, 'Failed to remove payment method'));
      setShowDeleteConfirm(false);
      setMethodToDelete(null);
    }
  }, [methodToDelete, deleteCard, deleteBank, showSuccess, showError, refetch, onMethodsUpdated]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setMethodToDelete(null);
  }, []);

  const isEmpty = paymentMethods.length === 0;

  const normalizeBrand = (brand?: string): any => {
    if (!brand) return 'unknown';
    const b = brand.toLowerCase();
    if (b.includes('visa')) return 'visa';
    if (b.includes('master') || b === 'mc') return 'mastercard';
    if (b.includes('amex')) return 'amex';
    if (b.includes('discover') || b === 'disc') return 'discover';
    if (b.includes('diners') || b === 'dnr') return 'diners';
    if (b === 'jcb') return 'jcb';
    if (b.includes('maestro') || b === 'maes') return 'maestro';
    if (b.includes('union') || b === 'up') return 'unionpay';
    return 'unknown';
  };

  const renderCard = (method: PaymentMethod) => {
    const isBank = method.type === 'ach' || method.type === 'bank' || (method as any).type === 'bankAccount';
    const brand = isBank ? 'bank' : normalizeBrand(method.brand || (method as any).accttype);
    const label = isBank
      ? (method.bankName || method.name || 'Bank Account')
      : (brand === 'unknown' ? (method.name || 'Card') : brand.charAt(0).toUpperCase() + brand.slice(1));

    return (
      <PaymentMethodCard
        key={String(method.sourceId || method.id)}
        brand={brand}
        label={label}
        lastFour={method.last4 || '****'}
        cardholderName={method.name}
        isDefault={!!method.isDefault}
        isExpired={!!method.isExpired}
        interactive
        onSetAsDefault={() => handleSetDefault(method)}
        onDelete={() => handleDeletePress(method)}
        isUpdating={isLoading}
        canDelete={!method.isDefault}
        style={styles.gridItem}
      />
    );
  };

  return (
    <>
      <View style={styles.container}>
        <View style={styles.content}>
          {isEmpty ? (
            <View style={styles.emptyState}>
              <View style={styles.emptyIconOuter}>
                <View style={styles.emptyIconGlow} />
                <View style={styles.emptyIconContainer}>
                  {CreditCardIcon && (
                    <CreditCardIcon width={28} height={28} color={colors.text.dimmer} />
                  )}
                </View>
              </View>
              <Text style={styles.emptyStateTitle}>No saved payment methods</Text>
              <Text style={styles.emptyStateSubtext}>
                You'll need to add a new payment method before making your next payment.
              </Text>
            </View>
          ) : (
            <View style={styles.methodsGrid}>
              {paymentMethods.map(renderCard)}
            </View>
          )}
        </View>
      </View>

      <ConfirmationModal
        visible={showDeleteConfirm}
        message="Remove saved payment method?"
        onCancel={handleDeleteCancel}
        onProceed={handleDeleteConfirm}
        cancelText="No, cancel"
        proceedText="Proceed"
      />
    </>
  );
};

const useManageWalletStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      container: {
        flex: 1,
      },
      content: {
        paddingHorizontal: 24,
        paddingTop: 24,
      },
      methodsGrid: {
        gap: 12,
      },
      gridItem: {
        width: '100%',
      },
      emptyState: {
        alignItems: 'center',
        paddingVertical: 40,
        gap: 12,
      },
      emptyIconOuter: {
        width: 64,
        height: 64,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 4,
      },
      emptyIconGlow: {
        position: 'absolute',
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: 'rgba(70,114,254,0.06)',
      },
      emptyIconContainer: {
        width: 52,
        height: 52,
        borderRadius: 16,
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.06)',
        alignItems: 'center',
        justifyContent: 'center',
      },
      emptyStateTitle: {
        ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
        color: colors.text.primary,
        textAlign: 'center',
      },
      emptyStateSubtext: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.muted,
        textAlign: 'center',
        lineHeight: 21,
        maxWidth: 260,
      },
    }),
  );

export default ManageWalletContent;
